import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

interface ResponsesByWorkload {
  workloadName: string;
  responses: number;
  completionRate: number;
}

interface QuestionAnswer {
  answer: string;
  count: number;
  percentage: number;
}

interface QuestionBreakdown {
  question: string;
  answers: QuestionAnswer[];
}

interface SurveyData {
  totalResponses: number;
  responsesByWorkload: ResponsesByWorkload[];
  questionBreakdown: QuestionBreakdown[];
}

interface SurveysReportProps {
  surveyData: SurveyData;
}

const SurveysReport: React.FC<SurveysReportProps> = ({
  surveyData
}) => {
  return (
    <div className="space-y-6">
      {/* Survey Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Responses</p>
          <p className="text-2xl font-bold text-gray-900">{surveyData.totalResponses}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Workloads with Responses</p>
          <p className="text-2xl font-bold text-indigo-600">{surveyData.responsesByWorkload.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Questions Analyzed</p>
          <p className="text-2xl font-bold text-green-600">{surveyData.questionBreakdown.length}</p>
        </div>
      </div>

      {/* Responses by Workload */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Responses by Workload</h3>
        {surveyData.responsesByWorkload.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={surveyData.responsesByWorkload}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="workloadName" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="responses" fill="#4F46E5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No workload response data available.
          </div>
        )}
      </div>

      {/* Question Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Response Breakdown by Question</h3>
        {surveyData.questionBreakdown.length === 0 ? (
          <p className="text-gray-500">No survey responses to analyze.</p>
        ) : (
          <div className="space-y-6">
            {surveyData.questionBreakdown.map((q, qi) => (
              <div key={qi} className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">{q.question}</h4>
                <div className="space-y-2">
                  {q.answers.map((a, ai) => (
                    <div key={ai} className="flex items-center">
                      <span className="w-32 text-sm text-gray-600 truncate">{a.answer}</span>
                      <div className="flex-1 mx-2 h-4 bg-gray-200 rounded">
                        <div
                          className="h-4 bg-indigo-500 rounded"
                          style={{ width: `${a.percentage}%` }}
                        />
                      </div>
                      <span className="w-16 text-sm text-gray-500 text-right">{a.count} ({a.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveysReport;
